/* ===========================================================
   Transporte de analytics da LANDING (Mixpanel) — decisions.md §34.
   No-op SEGURO sem VITE_MIXPANEL_TOKEN: o init só liga com token, e todo
   `track`/`identify` é guardado por flag + try/catch. Isso preserva o ethos
   offline-first do projeto (sem serviço, a landing continua 100% funcional) e
   evita poluir o projeto Mixpanel a partir do `npm run dev` (que roda o app
   completo, sem token). Token via env é atalho de protótipo — auditoria em
   docs/producao-v1.md (F10). Catálogo de eventos: docs/analytics-eventos.md.

   PERFORMANCE (perf): o Mixpanel (~430 KB / 128 KB gzip) NÃO entra no bundle
   inicial. Só o TIPO é importado (some no runtime); o código real é carregado
   sob demanda por `import()` em `initAnalytics`, agendado para quando a thread
   fica ociosa (requestIdleCallback) — fora do caminho crítico do first paint.
   Eventos disparados ANTES de o Mixpanel carregar entram numa fila e são
   reproduzidos no flush (não se perde o page view inicial).

   PII/LGPD: a medição de comportamento (anônima) roda sob LEGÍTIMO INTERESSE,
   sem opt-in — `ignore_dnt:false` respeita o "Não rastrear" do navegador (opt-out).
   PII (`identifyLead`: e-mail/nome) só vai no ENVIO da waitlist — consentido no
   próprio formulário. Ver ADR §34.
   =========================================================== */

// Só o TIPO (erasado no build → não puxa o módulo pro bundle). O runtime vem do
// import() dinâmico em initAnalytics.
type MixpanelLib = (typeof import("mixpanel-browser"))["default"];

// Token do projeto Mixpanel. Vazio em dev/app → analytics em modo no-op.
const TOKEN = (import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined)?.trim();
// Fase de lançamento (§33): sem VITE_APP_URL os CTAs caem na waitlist.
const APP_URL = (import.meta.env.VITE_APP_URL as string | undefined)?.trim() ?? "";

// Mixpanel SÓ em PRODUÇÃO (decisão do dono, jun/2026). Duas camadas:
//  1. Build: `VITE_MIXPANEL_TOKEN` é variável PROTECTED do GitLab — só é exposta
//     no pipeline da `main` (deploy de produção), nunca em preview/MR. Sem token,
//     `initAnalytics` é no-op.
//  2. Runtime: mesmo que um token vaze para um bundle não-produtivo, só inicializa
//     no HOSTNAME de produção. Assim test, local (localhost) e URL temporária de
//     preview (*.pages.dev) NÃO disparam analytics — independe da config de CI.
const PROD_HOSTS = new Set(["lucraamais.com.br", "www.lucraamais.com.br"]);
function emProducao(): boolean {
  try {
    return typeof window !== "undefined" && PROD_HOSTS.has(window.location.hostname);
  } catch {
    return false;
  }
}

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
const CLICK_IDS = ["gclid", "fbclid"] as const; // Google/Meta Ads click ids

/** Parâmetros de aquisição (UTM + click ids + referrer) — fixados como super
 *  properties, então acompanham TODOS os eventos da sessão (atribuição). */
function acquisitionProps(): Record<string, string> {
  const props: Record<string, string> = {};
  try {
    const qs = new URLSearchParams(window.location.search);
    for (const k of [...UTM_KEYS, ...CLICK_IDS]) {
      const v = qs.get(k);
      if (v) props[k] = v;
    }
    const ref = document.referrer;
    if (ref) {
      props.referrer = ref;
      try {
        props.referrer_domain = new URL(ref).hostname;
      } catch {
        /* referrer malformado — ignora o domínio */
      }
    }
  } catch {
    /* ambiente sem window (SSG/prerender) — sem aquisição */
  }
  return props;
}

// Estado do carregamento sob demanda.
let mp: MixpanelLib | null = null; // instância, quando carregada
let ready = false; // init concluído
let carregando = false; // import() em voo (evita disparar duas vezes)

// Fila de chamadas feitas ANTES de o Mixpanel carregar. Só enfileira quando
// REALMENTE vamos inicializar (prod + token) — em dev/preview segue no-op puro.
// Cap defensivo: se o import() falhar (rede), a fila não cresce sem limite.
const fila: Array<(m: MixpanelLib) => void> = [];
const FILA_MAX = 50;

function deveInicializar(): boolean {
  return Boolean(TOKEN) && typeof window !== "undefined" && emProducao();
}

/** Executa `fn` com o Mixpanel: pronto → agora; carregando/ainda não →
 *  enfileira; sem token/fora de prod → no-op. Nunca lança. */
function comMixpanel(fn: (m: MixpanelLib) => void): void {
  if (ready && mp) {
    try {
      fn(mp);
    } catch {
      /* analytics nunca interrompe o usuário */
    }
    return;
  }
  if (deveInicializar() && fila.length < FILA_MAX) fila.push(fn);
}

/** Agenda o carregamento para quando a thread principal estiver ociosa (fora do
 *  caminho crítico do first paint). Fallback: timeout curto. */
function agendarOcioso(fn: () => void): void {
  const w = window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
  };
  if (typeof w.requestIdleCallback === "function") w.requestIdleCallback(fn, { timeout: 3000 });
  else window.setTimeout(fn, 1200);
}

/** Inicializa o Mixpanel uma única vez (carregando o módulo sob demanda). No-op
 *  sem token / fora de produção. Nunca lança.
 *  `extra`: super properties fixas adicionais — ex.: `{ app: "landing" | "demo" }`
 *  para segmentar as duas superfícies públicas no mesmo projeto (ADR §37, F3). */
export function initAnalytics(extra?: Record<string, unknown>): void {
  if (ready || carregando || !deveInicializar()) return;
  carregando = true;
  agendarOcioso(() => {
    import("mixpanel-browser")
      .then(({ default: mixpanel }) => {
        try {
          mixpanel.init(TOKEN!, {
            api_host: "https://api.mixpanel.com", // região: EUA (decisão §34; EU = api-eu.mixpanel.com)
            debug: import.meta.env.DEV,
            track_pageview: false, // page views são controlados por rota (SPA)
            persistence: "localStorage",
            ignore_dnt: false, // respeita Do Not Track do navegador (LGPD)
          });
          mixpanel.register({
            ...acquisitionProps(),
            fase_lancamento: APP_URL ? "app" : "waitlist",
            ...extra,
          });
          mp = mixpanel;
          ready = true;
          // flush da fila na ordem em que os eventos aconteceram
          for (const fn of fila) {
            try {
              fn(mixpanel);
            } catch {
              /* idem — nunca interrompe o usuário */
            }
          }
          fila.length = 0;
        } catch {
          carregando = false; // init falhou: não trava tentativas futuras
        }
      })
      .catch(() => {
        carregando = false; // import falhou (rede): mantém no-op seguro
      });
  });
}

/** Dispara um evento. Enfileira se o Mixpanel ainda não carregou. Nunca lança. */
export function track(event: string, props?: Record<string, unknown>): void {
  comMixpanel((m) => m.track(event, props));
}

/** Fixa super properties adicionais na sessão (acompanham TODOS os eventos
 *  seguintes — ex.: `demo_run` para medir reincidência ao longo do funil).
 *  Enfileira se o Mixpanel ainda não carregou. Nunca lança. */
export function registrarSuperProps(props: Record<string, unknown>): void {
  comMixpanel((m) => m.register(props));
}

export interface LeadIdentity {
  email: string;
  nome: string;
  loja: string;
  faixaFaturamento: string;
}

/** Conecta a jornada anônima ao lead capturado (People). PII consentida no
 *  formulário da waitlist — chamar só no envio bem-sucedido. */
export function identifyLead(lead: LeadIdentity): void {
  comMixpanel((m) => {
    m.identify(lead.email);
    m.people.set({
      $email: lead.email,
      $name: lead.nome,
      // mesmo nome da super-prop do evento (Demo Configurada) — perfil e eventos
      // falam a mesma língua, então dá pra cruzar People × eventos sem traduzir.
      loja_nome: lead.loja,
      faixa_faturamento: lead.faixaFaturamento,
    });
  });
}
